import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Code2, Cpu, Database, Server, Zap, Grid3X3, Mail } from 'lucide-react';

interface Competenza {
  icon: React.ReactNode;
  text: string;
  color: string;
}


const Hero: React.FC = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [badgeIndex, setBadgeIndex] = useState(0);

  const competenze: Competenza[] = [
    { icon: <Code2 size={18} />, text: '.NET 9 Expert', color: 'text-primary-400' },
    { icon: <Cpu size={18} />, text: 'IoT & RFID', color: 'text-indigo-400' },
    { icon: <Database size={18} />, text: 'SQL & Databases', color: 'text-emerald-400' },
    { icon: <Server size={18} />, text: 'Infrastructure', color: 'text-blue-400' },
    { icon: <Zap size={18} />, text: 'Full Stack Dev', color: 'text-yellow-400' },
    { icon: <Grid3X3 size={18} />, text: 'Web & UI/UX', color: 'text-pink-400' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const heroHeight = window.innerHeight;
      const progress = Math.min(scrollTop / (heroHeight * 0.8), 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setBadgeIndex((prev) => (prev + 1) % competenze.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [competenze.length]);

  const handleScrollTo = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    else window.location.hash = id;
  };

  const size = 300 - scrollProgress * 236;
  const opacity = 1 - scrollProgress;

  const currentBadge = competenze[badgeIndex];
  const nextBadge = competenze[(badgeIndex + 1) % competenze.length];

  return (
    <>
      <section id="hero" className="relative min-h-screen flex items-center justify-center pt-16 overflow-hidden dark:bg-[#0c1322]">
        {/* Sfondo: foto circuiti (solo dark mode) */}
        <div className="absolute inset-0 hidden dark:block bg-[url('/images/circuit-bg.jpg')] bg-cover bg-center opacity-20 grayscale brightness-50 -z-10 pointer-events-none"></div>

        {/* Blob luminosi */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-600/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col-reverse lg:flex-row items-center gap-12 w-full">
          <motion.div
            className="lg:w-1/2 text-center lg:text-left"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block px-4 py-2 rounded-full bg-gray-100 dark:bg-slate-800/80 border border-gray-200 dark:border-slate-700 text-primary-600 dark:text-primary-400 text-sm font-semibold mb-6">
              Senior Full Stack Developer & System Integrator
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white mb-6 leading-tight">
              Ciao, sono <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #c3c0ff, #4f46e5)' }}>Walter</span>
              <br />
              <span className="text-2xl md:text-3xl font-semibold text-gray-700 dark:text-slate-300">Sviluppatore C#/.NET: collego macchine industriali e software.</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 dark:text-slate-400 mb-8 max-w-2xl mx-auto lg:mx-0">
              Trasformo macchine industriali in sistemi smart.<br />
              Più controllo, meno errori, dati sempre disponibili.
            </p>
            <ul className="mb-8 space-y-2 text-gray-600 dark:text-slate-400">
              <li>• 20+ anni tra software, sistemi e hardware industriale</li>
              <li>• RFID, PLC Siemens, comunicazioni seriali, SQL Server</li>
              <li>• Applicazioni .NET e dashboard web (React)</li>
            </ul>
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
              <a
                href="#contact"
                onClick={(e) => handleScrollTo(e, 'contact')}
                className="w-full sm:w-auto px-8 py-4 text-white rounded-lg font-semibold transition-all shadow-lg shadow-indigo-900/50 flex items-center justify-center gap-2"
                style={{ backgroundImage: 'linear-gradient(135deg, #c3c0ff, #4f46e5)' }}
              >
                Parliamone <Mail size={20} />
              </a>
              <a
                href="#projects"
                onClick={(e) => handleScrollTo(e, 'projects')}
                className="w-full sm:w-auto px-8 py-4 rounded-lg font-semibold transition-all border border-gray-300 dark:border-slate-600 text-gray-800 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center justify-center gap-2"
              >
                Scopri i Progetti <ChevronDown size={20} />
              </a>
            </div>
          </motion.div>

          <motion.div
            className="lg:w-1/2 relative flex justify-center"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div style={{ width: `${size}px`, height: `${size}px`, opacity }} className="relative transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-tr from-primary-500 to-indigo-500 rounded-full blur-2xl opacity-50"></div>

              <div className="relative w-full h-full rounded-full overflow-hidden border-4 border-slate-800 shadow-2xl">
                <img
                  src="/walter-profile.webp"
                  alt="Walter Rodeghiero, sviluppatore C#/.NET e integratore di sistemi industriali"
                  width={640}
                  height={640}
                  {...{ fetchpriority: "high" }}
                  draggable={false}
                  className="w-full h-full object-cover select-none"
                />
              </div>

              {opacity > 0.3 && (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={badgeIndex}
                    className="absolute -top-4 -right-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 p-3 rounded-xl shadow-xl flex items-center gap-2"
                    style={{ opacity }}
                    initial={{ scale: 0, y: -10 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0, y: 10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <span className={currentBadge.color}>{currentBadge.icon}</span>
                    <span className="font-bold text-sm text-gray-900 dark:text-white">{currentBadge.text}</span>
                  </motion.div>
                </AnimatePresence>
              )}

              {opacity > 0.3 && (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`bottom-${badgeIndex}`}
                    className="absolute -bottom-4 -left-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 p-3 rounded-xl shadow-xl flex items-center gap-2"
                    style={{ opacity }}
                    initial={{ scale: 0, y: 10 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0, y: -10 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                  >
                    <span className={nextBadge.color}>{nextBadge.icon}</span>
                    <span className="font-bold text-sm text-gray-900 dark:text-white">{nextBadge.text}</span>
                  </motion.div>
                </AnimatePresence>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Hero;
